import { useEffect, useMemo } from 'react';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { useInternalLayersFeatures } from 'hooks/internalLayerVersion/useListInternalLayerVersionFeaturesQuery';
import { syncInternalLayers } from './syncInternalLayers';
import type { ArcgisMapView } from './arcgisView';
import type { InternalLayerVersion } from 'hooks/internalLayerVersion/types';

/**
 * Syncs internal ArcGIS GraphicsLayer(s) with feature data from PRATUS hosted
 * internal layer versions and manages their lifecycle. Shows one
 * internal_layer_version per internal_layer controlled by the version selected
 * in the sidebar.
 */
export function useInternalLayerSync({
  mapView,
  internalLayerVisibilityById,
  incidentFilterId,
  selectedInternalLayerVersionByInternalLayerId,
  layersRef,
  includeLayerIds,
}: {
  mapView: ArcgisMapView | null;
  internalLayerVisibilityById: Map<string, boolean | null>;
  incidentFilterId: string | null;
  selectedInternalLayerVersionByInternalLayerId: Record<
    string,
    InternalLayerVersion
  >;
  layersRef: React.MutableRefObject<Map<string, GraphicsLayer>>;
  /**
   * Restricts the sync to these internal layer ids. The main map syncs every
   * layer the incident has and hides the ones that are off, but a small
   * embedded map only wants the handful that were picked for it, so it passes a
   * set here. Left undefined, every layer syncs, which is what the main map
   * does.
   */
  includeLayerIds?: ReadonlySet<string>;
}) {
  const { internalLayerVersionsMap: allVersionsMap, isLoading } =
    useInternalLayersFeatures(
      incidentFilterId,
      selectedInternalLayerVersionByInternalLayerId
    );

  const internalLayerVersionsMap = useMemo(() => {
    if (!includeLayerIds) return allVersionsMap;
    return new Map(
      Array.from(allVersionsMap).filter(([, entry]) =>
        includeLayerIds.has(String(entry.internalLayer.id))
      )
    );
  }, [allVersionsMap, includeLayerIds]);

  // useQueries hands back a new array every render, so this map changes
  // identity on every render of the host. Keep the sync incremental: tearing
  // every layer down per render redraws them mid-drag and drops pans.
  useEffect(() => {
    if (!mapView?.map) return;

    syncInternalLayers({
      map: mapView.map,
      internalLayerVersionsMap,
      internalLayerVisibilityById,
      layersRef,
    });
  }, [
    mapView,
    internalLayerVersionsMap,
    internalLayerVisibilityById,
    layersRef,
  ]);

  useEffect(() => {
    const layers = layersRef.current;
    return () => {
      if (mapView?.map) {
        layers.forEach((layer) => mapView.map.remove(layer));
      }
      layers.clear();
    };
  }, [mapView, layersRef]);

  return { isLoading };
}
